import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, MessageCircle } from "lucide-react";

const UPDATED = "31 de agosto de 2026";

export const Route = createFileRoute("/termos-de-uso")({
  head: () => ({
    meta: [
      { title: "Termos de Uso — Kentro Sistemas" },
      {
        name: "description",
        content:
          "Termos de Uso da Kentro: licenciamento do software de gestão de atendimento, canais do omnichannel (WhatsApp, Instagram, Facebook, E-mail, SMS e RCS), integrações de terceiros, disponibilidade, pagamento e responsabilidades.",
      },
      { property: "og:title", content: "Termos de Uso — Kentro Sistemas" },
      {
        property: "og:description",
        content:
          "Condições de uso da plataforma Kentro de gestão de atendimento omnichannel, incluindo integrações com serviços de terceiros.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://kentro.com.br/termos-de-uso" },
    ],
    links: [{ rel: "canonical", href: "https://kentro.com.br/termos-de-uso" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Termos de Uso — Kentro Sistemas",
          url: "https://kentro.com.br/termos-de-uso",
          inLanguage: "pt-BR",
          dateModified: "2026-08-31",
          publisher: { "@type": "Organization", name: "Kentro Sistemas Inteligentes" },
        }),
      },
    ],
  }),
  component: TermosPage,
});

function TermosPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <FileText className="size-3.5" /> Documento contratual · Versão 1.0
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            Termos de <span className="gradient-text">Uso</span>
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">Última atualização: {UPDATED}.</p>
          <p className="mt-5 max-w-3xl text-base text-muted-foreground">
            Estes Termos de Uso regulam o acesso e a utilização do site, da plataforma e dos
            produtos da <strong className="text-foreground">Kentro Sistemas Inteligentes</strong>{" "}
            (razão social Barbalho Solutions Ltda., CNPJ nº 43.406.507/0001-52, com sede na
            Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP, CEP 06454-000) — o
            “Software Kentro”. Ao acessar ou utilizar nossos serviços, você declara ter lido e
            aceito integralmente estes Termos, que devem ser lidos em conjunto com o Contrato de
            Cessão de Direito de Uso (Licença) firmado com o cliente, com a{" "}
            <Link to="/privacidade" className="text-primary-glow hover:underline">
              Política de Privacidade
            </Link>
            , com a{" "}
            <Link to="/lgpd" className="text-primary-glow hover:underline">
              Política de Tratamento de Dados (LGPD)
            </Link>{" "}
            e com a{" "}
            <Link to="/politica-de-cookies" className="text-primary-glow hover:underline">
              Política de Cookies
            </Link>
            .
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <nav aria-label="Sumário" className="mb-12 rounded-2xl border border-border bg-card/40 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Sumário
          </h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {[
              ["aceitacao", "Aceitação e abrangência"],
              ["definicoes", "Definições e categorias de usuários"],
              ["licenca", "Licença de uso e propriedade intelectual"],
              ["omnichannel", "Canais do omnichannel Kentro"],
              ["terceiros", "Integrações e serviços de terceiros"],
              ["rcs", "Regras específicas de RCS, SMS e e-mail"],
              ["disponibilidade", "Disponibilidade e suporte"],
              ["obrigacoes", "Obrigações, proibições e uso irregular"],
              ["pagamento", "Pagamento, reajuste e prazo"],
              ["dados", "Dados, privacidade e exclusão após o cancelamento"],
              ["responsabilidade", "Limitação de responsabilidade"],
              ["gerais", "Disposições gerais, alterações e foro"],
            ].map(([id, label]) => (
              <li key={id}>
                <a className="hover:text-foreground" href={`#${id}`}>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="prose-kentro space-y-10 text-foreground [&_a]:text-primary-glow [&_strong]:text-foreground">
          <Block id="aceitacao" title="1. Aceitação e abrangência">
            <p>
              1.1. O acesso ou a utilização, temporária ou continuada, do site, da plataforma, dos
              módulos, das APIs, dos webhooks e de quaisquer produtos da Kentro implica aceitação
              expressa, integral e irrestrita destes Termos, na sua versão vigente.
            </p>
            <p>
              1.2. Quem não concordar com estes Termos não deve acessar nem utilizar a plataforma.
            </p>
            <p>
              1.3. Estes Termos observam a legislação brasileira aplicável, especialmente o Marco
              Civil da Internet (Lei nº 12.965/2014 e Decreto nº 8.771/2016), a Lei Geral de
              Proteção de Dados — LGPD (Lei nº 13.709/2018) e o Código de Defesa do Consumidor,
              quando aplicável.
            </p>
            <p>
              1.4. Ao aceitar estes Termos, você declara ter capacidade civil e poderes para
              representar a pessoa jurídica em nome da qual atua, bem como dispor dos equipamentos,
              conexão e conhecimentos técnicos necessários ao uso adequado da plataforma.
            </p>
            <p>
              1.5. Estes Termos valem para todos os sistemas, ferramentas, módulos e produtos da
              Kentro que não possuam termos próprios e específicos. Em caso de conflito, prevalece o
              Contrato de Licenciamento assinado entre as partes.
            </p>
          </Block>

          <Block id="definicoes" title="2. Definições e categorias de usuários">
            <ul>
              <li>
                <strong>Usuário(a):</strong> qualquer pessoa que acesse áreas públicas do site ou
                dos produtos da Kentro, com ou sem identificação.
              </li>
              <li>
                <strong>Cliente:</strong> pessoa física ou jurídica que contratou o licenciamento do
                Software Kentro e acessa áreas restritas da plataforma.
              </li>
              <li>
                <strong>Operador(a) / Atendente:</strong> usuário nomeado pelo Cliente para operar a
                plataforma sob credenciais individuais.
              </li>
              <li>
                <strong>Contato final:</strong> pessoa que interage com o Cliente pelos canais
                conectados à plataforma (por exemplo, um consumidor no WhatsApp).
              </li>
              <li>
                <strong>Áreas públicas e restritas:</strong> respectivamente, conteúdos de livre
                acesso e conteúdos acessíveis somente mediante credenciais.
              </li>
            </ul>
          </Block>

          <Block id="licenca" title="3. Licença de uso e propriedade intelectual">
            <p>
              3.1. A Kentro concede ao Cliente licença de uso <strong>onerosa, temporária, não
              exclusiva, revogável e intransferível</strong> do Software Kentro, exclusivamente para
              a gestão dos atendimentos do próprio Cliente, nos limites do plano contratado (número
              de usuários, módulos e armazenamento).
            </p>
            <p>
              3.2. O Software Kentro e todos os direitos de propriedade intelectual relacionados
              permanecem de titularidade exclusiva da Kentro ou dos terceiros que a licenciam. Nada
              nestes Termos transfere titularidade, código-fonte ou qualquer direito além do uso.
            </p>
            <p>3.3. É vedado ao Cliente e ao Usuário:</p>
            <ul>
              <li>
                ceder, sublicenciar, vender, locar, dar em garantia, doar ou transferir a terceiros,
                total ou parcialmente, a licença de uso, sem autorização prévia e expressa da
                Kentro;
              </li>
              <li>
                modificar, ampliar, alterar, praticar engenharia reversa, descompilar, decodificar
                ou desmontar o Software Kentro; quaisquer produtos derivados serão de propriedade
                exclusiva da Kentro;
              </li>
              <li>
                copiar, redistribuir ou explorar comercialmente telas, layouts, marcas, documentação
                ou materiais da Kentro;
              </li>
              <li>
                remover avisos de titularidade, burlar limites técnicos de uso, de volume ou de
                segurança.
              </li>
            </ul>
            <p>
              3.4. A violação do item 3.3 constitui infração a direitos de propriedade intelectual e
              sujeita o infrator às sanções civis, administrativas e criminais aplicáveis, além da
              reparação integral das perdas e danos.
            </p>
          </Block>

          <Block id="omnichannel" title="4. Canais do omnichannel Kentro">
            <p>
              4.1. A plataforma Kentro centraliza, em uma única interface de atendimento, os
              seguintes canais:
            </p>
            <ul>
              <li>
                <strong>WhatsApp</strong> (API Oficial da Meta / WhatsApp Business Platform e demais
                formas de conexão suportadas);
              </li>
              <li>
                <strong>Instagram</strong> (Direct Messages e comentários, via APIs da Meta);
              </li>
              <li>
                <strong>Facebook</strong> (Messenger e comentários, via APIs da Meta);
              </li>
              <li>
                <strong>E-mail</strong> (envio e recebimento por provedores de e-mail e serviços de
                entrega transacional);
              </li>
              <li>
                <strong>SMS</strong> (mensagens de texto por meio de operadoras e agregadores);
              </li>
              <li>
                <strong>RCS</strong> (Rich Communication Services, mensagens verificadas com
                remetente identificado, cards, imagens e botões nativos).
              </li>
            </ul>
            <p>
              4.2. Os canais disponíveis dependem do plano contratado, da aprovação de cadastro nas
              plataformas dos provedores e das regras de cada canal, que podem mudar a qualquer
              momento por decisão do respectivo provedor.
            </p>
            <p>
              4.3. A Kentro <strong>integra</strong> esses canais, mas não é a proprietária deles. Os
              canais são operados por terceiros (Meta, provedores de e-mail, operadoras móveis,
              agregadores de SMS/RCS e demais parceiros), conforme a seção 5.
            </p>
          </Block>

          <Block id="terceiros" title="5. Integrações e serviços de terceiros">
            <p>
              5.1. O Software Kentro é um sistema integrado que depende de aplicativos, APIs e
              infraestrutura de terceiros. O WhatsApp, o Instagram e o Facebook, por exemplo, são
              serviços de propriedade da Meta Platforms; SMS e RCS dependem de operadoras móveis e
              agregadores; o e-mail depende de provedores de envio e recebimento.
            </p>
            <p>
              5.2. A Kentro <strong>não possui ingerência ou controle</strong> sobre esses serviços
              de terceiros. Suspensões, bloqueios, mudanças de política, alterações de preço,
              limitações de volume, filtros antispam, indisponibilidades ou descontinuações
              promovidas pelos provedores podem impactar, parcial ou integralmente, o funcionamento
              da plataforma, sem que isso gere dever de reembolso, desconto ou indenização pela
              Kentro.
            </p>
            <p>
              5.3. Cabe ao Cliente escolher quais integrações deseja utilizar e{" "}
              <strong>ler, conhecer, concordar e cumprir</strong> os termos de uso, as políticas
              comerciais e as políticas de privacidade de cada provedor terceiro, sob pena das
              sanções aplicadas por esses provedores (por exemplo, bloqueio de número de WhatsApp por
              qualidade baixa ou por envio de mensagens não solicitadas).
            </p>
            <p>
              5.4. Tarifas cobradas diretamente pelos provedores (por exemplo, tarifas por conversa,
              por mensagem ou por destinatário no WhatsApp, SMS e RCS) são de responsabilidade do
              Cliente e não estão incluídas no valor do licenciamento, salvo previsão expressa na
              proposta comercial.
            </p>
            <p>
              5.5. O Cliente pode conectar, por sua conta e iniciativa, serviços externos por meio
              das APIs e webhooks da Kentro (CRMs, ERPs, automações e serviços de Inteligência
              Artificial / LLMs). Nesses casos, a contratação, a configuração e as consequências do
              uso são de responsabilidade exclusiva do Cliente, que atua como Controlador dos dados
              enviados a esses terceiros.
            </p>
            <p>
              5.6. Data centers e provedores de nuvem de referência (como Google, Oracle, Amazon Web
              Services) e serviços de CDN e segurança (como Cloudflare) são utilizados para hospedar
              e proteger a plataforma, com alta disponibilidade contratada.
            </p>
          </Block>

          <Block id="rcs" title="6. Regras específicas de RCS, SMS e e-mail">
            <p>
              6.1. <strong>RCS.</strong> O RCS é a evolução do SMS e permite mensagens verificadas
              com identidade do remetente, links, cards com imagem, título, texto e botões nativos
              (abrir URL, ligar, adicionar ao calendário, localização, entre outros). Na Kentro, o
              RCS pode ser utilizado em duas naturezas típicas:
            </p>
            <ul>
              <li>
                <strong>RCS básico:</strong> mensagem de texto verificada com link, com confirmação
                de entrega e leitura, indicada para avisos, NPS, confirmações, lembretes e chamadas
                para o WhatsApp, com custo otimizado para alto volume;
              </li>
              <li>
                <strong>RCS de notificação (card):</strong> mensagem com card(s) e botões nativos,
                indicada para ofertas, cobranças, promoções e reengajamento, com maior impacto visual
                e melhor taxa de cliques.
              </li>
            </ul>
            <p>
              6.2. O envio de RCS depende de o destinatário possuir aparelho e aplicativo de
              mensagens compatíveis e com RCS ativo, de homologação do remetente (marca verificada)
              junto às operadoras/provedores e de disponibilidade na respectiva operadora. Quando o
              RCS não estiver disponível, poderá haver fallback para SMS, conforme configuração e
              contratação.
            </p>
            <p>
              6.3. A cobrança de RCS e SMS é feita por destinatário/mensagem entregue, conforme
              tabela do provedor e da proposta comercial vigente, cabendo ao Cliente controlar seu
              volume de disparos.
            </p>
            <p>
              6.4. <strong>Consentimento e opt-out.</strong> O Cliente é o único responsável por
              possuir base legal para contatar os destinatários (consentimento, execução de contrato
              ou legítimo interesse, conforme o caso), por manter registro dessa base legal, por
              respeitar solicitações de descadastramento (opt-out) e por não realizar envios em massa
              não solicitados (spam). O descumprimento pode gerar bloqueio do remetente pelas
              operadoras e provedores e rescisão motivada pela Kentro.
            </p>
            <p>
              6.5. <strong>E-mail.</strong> A entrega de e-mails depende de reputação de domínio,
              autenticação (SPF, DKIM, DMARC) e filtros dos provedores de destino, não sendo possível
              garantir entrega na caixa de entrada.
            </p>
          </Block>

          <Block id="disponibilidade" title="7. Disponibilidade e suporte">
            <p>
              7.1. A Kentro mantém contratos com data centers de referência internacional que
              garantem até <strong>98% (noventa e oito por cento) de disponibilidade anual</strong> do
              Software Kentro.
            </p>
            <p>
              7.2. Indisponibilidades relacionadas exclusivamente ao Software Kentro poderão ser
              descontadas, de forma proporcional ao tempo indisponível, da remuneração devida,
              conforme ajuste escrito entre as partes. Indisponibilidades causadas por terceiros
              (canais, operadoras, provedores, conexão do Cliente) não geram desconto.
            </p>
            <p>
              7.3. O suporte é prestado em horário comercial, nos termos do plano contratado, e se
              limita a questões técnicas da própria plataforma. Problemas originados em aplicativos,
              softwares e sistemas de terceiros devem ser tratados diretamente com o suporte desses
              terceiros.
            </p>
            <p>
              7.4. A Kentro pode realizar manutenções programadas e evolutivas, buscando janelas de
              menor impacto, além de suspender total ou parcialmente o serviço em caso de suspeita
              fundada de incidente de segurança, com transparência sobre o ocorrido.
            </p>
            <p>
              7.5. Em períodos de teste (trial), a Kentro poderá encerrar o licenciamento caso
              conclua não haver viabilidade técnica, sem multa ou pendência financeira.
            </p>
          </Block>

          <Block id="obrigacoes" title="8. Obrigações, proibições e uso irregular">
            <p>
              8.1. O Cliente e seus usuários obrigam-se a fornecer informações verdadeiras, exatas e
              atualizadas, a manter sigilo das credenciais de acesso e a responder pelos atos
              praticados com elas.
            </p>
            <p>
              8.2. Ao inserir dados de terceiros na plataforma, o Cliente declara ser legítimo
              detentor ou possuir autorização/base legal para tanto, respondendo integralmente por
              dados informados de forma irregular.
            </p>
            <p>
              8.3. Adotamos política de <strong>tolerância zero</strong> para condutas abusivas ou
              ilícitas. Serão imediatamente suspensas e poderão ser rescindidas as contas utilizadas
              para: transgredir a legislação aplicável; ameaçar, intimidar, fraudar ou obter proveito
              ilícito; dissimular a origem de mensagens ou identidade do remetente; disparar
              mensagens não solicitadas em massa; armazenar dados pessoais sem base legal; promover
              atos ilegais, discriminatórios, ofensivos ou de invasão de privacidade; veicular
              conteúdo pornográfico ou que viole direitos de terceiros.
            </p>
            <p>
              8.4. Ao encerrar o acesso ou o contrato, o Cliente deve apagar informações, materiais e
              softwares da Kentro em sua posse, em formato eletrônico ou impresso.
            </p>
          </Block>

          <Block id="pagamento" title="9. Pagamento, reajuste e prazo">
            <p>
              9.1. O licenciamento é remunerado conforme a tabela vigente e a proposta comercial
              aceita pelo Cliente, considerando número de usuários, plano, módulos, armazenamento
              padrão e serviços de implantação/treinamento, em modalidade pré-paga, salvo ajuste
              diverso.
            </p>
            <p>
              9.2. Alterações no número de usuários, plano ou armazenamento devem ser tratadas com a
              Kentro e podem implicar revisão de valores conforme a tabela vigente.
            </p>
            <p>
              9.3. Os valores são reajustados anualmente pela variação positiva do{" "}
              <strong>IPCA/IBGE</strong> (ou índice que o substitua), contados da assinatura do
              contrato.
            </p>
            <p>
              9.4. Findo eventual prazo de permanência mínima, o contrato vigora por prazo
              indeterminado e pode ser resilido por qualquer das partes mediante{" "}
              <strong>aviso prévio de 30 (trinta) dias</strong>, ressalvada a cobrança de valores em
              aberto.
            </p>
            <p>
              9.5. O inadimplemento autoriza a suspensão do acesso, o protesto do título, a inscrição
              em órgãos de proteção ao crédito e a cobrança judicial, respondendo o Cliente por
              custas e honorários, na forma do contrato.
            </p>
          </Block>

          <Block id="dados" title="10. Dados, privacidade e exclusão após o cancelamento">
            <p>
              10.1. No tratamento dos dados inseridos na plataforma, o <strong>Cliente é
              Controlador</strong> e a <strong>Kentro é Operadora</strong>, nos termos do art. 5º, VI
              e VII, da LGPD. Detalhes completos estão na{" "}
              <Link to="/lgpd" className="text-primary-glow hover:underline">
                Política de Tratamento de Dados (LGPD)
              </Link>{" "}
              e na{" "}
              <Link to="/privacidade" className="text-primary-glow hover:underline">
                Política de Privacidade
              </Link>
              .
            </p>
            <p>
              10.2. <strong>Exclusão definitiva dos dados do Cliente:</strong> os dados gerados pelo
              Cliente dentro do sistema (conversas, contatos, anexos, registros de atendimento e
              demais conteúdos) são apagados e destruídos do banco de dados de forma definitiva{" "}
              <strong>em até 1 (um) dia útil após o encerramento do contrato</strong> — isto é, após
              o cumprimento do aviso prévio de 30 dias, a eliminação ocorre em até 24 horas úteis,
              sem possibilidade de recuperação. Cabe ao Cliente exportar previamente o que desejar
              preservar.
            </p>
            <p>
              10.3. <strong>Registros de acesso e logs.</strong> Mesmo após a exclusão dos dados do
              Cliente, a Kentro poderá manter, por obrigação legal e para fins de segurança,
              registros de acesso a aplicações (Marco Civil da Internet), logs de auditoria,
              documentos fiscais e demais dados cuja retenção seja exigida por lei, desvinculados da
              operação do Cliente e acessíveis apenas a pessoas autorizadas.
            </p>
            <p>
              10.4. As partes obrigam-se a comunicar incidentes de segurança envolvendo dados
              pessoais em até 3 (três) dias úteis da ciência, com as informações previstas na
              regulação da ANPD. Contato do Encarregado (DPO):{" "}
              <a href="mailto:dpo@kentro.com.br">dpo@kentro.com.br</a>.
            </p>
          </Block>

          <Block id="responsabilidade" title="11. Limitação de responsabilidade">
            <p>
              11.1. Salvo garantias expressamente formalizadas em contrato, a plataforma é fornecida
              sem garantia de resultado, precisão ou sucesso comercial, cabendo ao Cliente a execução
              de seus planos, estratégias e atividades.
            </p>
            <p>
              11.2. A responsabilidade da Kentro por danos especiais, eventuais, indiretos, lucros
              cessantes, perda de dados, paralisação de trabalho ou perdas comerciais, comprovadamente
              causados pelo uso do sistema, fica <strong>limitada aos valores efetivamente pagos</strong>{" "}
              pelo Cliente à Kentro em razão do contrato.
            </p>
            <p>
              11.3. A Kentro não responde por sites, aplicativos, widgets, serviços ou softwares de
              terceiros integrados à plataforma, nem por transações financeiras realizadas em
              ambientes de terceiros.
            </p>
            <p>
              11.4. Materiais e comunicações podem conter erros técnicos, tipográficos ou
              fotográficos, que serão corrigidos pontualmente conforme identificados.
            </p>
          </Block>

          <Block id="gerais" title="12. Disposições gerais, alterações e foro">
            <p>
              12.1. A Kentro pode alterar estes Termos a qualquer tempo, especialmente para adequação
              legal, regulatória ou de segurança. As alterações entram em vigor com a publicação
              nesta página, cabendo ao Cliente consultá-la periodicamente. A versão vigente estará
              sempre disponível em{" "}
              <a href="https://kentro.com.br/termos-de-uso">kentro.com.br/termos-de-uso</a>.
            </p>
            <p>
              12.2. A eventual invalidade ou inaplicabilidade de qualquer disposição não afeta a
              validade das demais.
            </p>
            <p>
              12.3. O Cliente autoriza o uso de seu nome e marca como referência no site e nas mídias
              sociais da Kentro durante a vigência do contrato, em licença gratuita e livre de
              royalties, ressalvada solicitação expressa em contrário.
            </p>
            <p>
              12.4. O Cliente pode receber comunicados, e-mails e newsletters da Kentro, podendo
              solicitar o descadastramento a qualquer momento.
            </p>
            <p>
              12.5. As partes obrigam-se ao sigilo sobre informações confidenciais durante a vigência
              do contrato e por 5 (cinco) anos após seu encerramento.
            </p>
            <p>
              12.6. Conflitos oriundos do contrato de licenciamento serão resolvidos por juízo
              arbitral, nos termos da Lei nº 9.307/96, junto à Câmara de Conciliação, Mediação e
              Arbitragem de Montes Claros/MG. Para as relações não abrangidas pela cláusula
              compromissória, fica eleito o foro da Comarca de Montes Claros/MG, ressalvadas as
              hipóteses legais de competência do domicílio do consumidor.
            </p>
          </Block>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h2 className="text-lg font-semibold">Documentos relacionados</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Estes Termos integram um conjunto de documentos. Consulte também:
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/lgpd"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <FileText className="size-4" /> Política de Tratamento de Dados (LGPD)
            </Link>
            <Link
              to="/privacidade"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <FileText className="size-4" /> Política de Privacidade
            </Link>
            <Link
              to="/ouvidoria"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <MessageCircle className="size-4" /> Canal de Denúncias
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Block({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground [&_li]:my-1 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
